import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  ChevronRight,
  Copy,
  ExternalLink,
  Image,
  Loader2,
  RefreshCw,
  Search,
  Sparkles,
  Trash2,
  Upload,
} from 'lucide-react';
import { Card } from '../../../../ui/card';
import { Button } from '../../../../ui/button';
import { Input } from '../../../../ui/input';
import { Switch } from '../../../../ui/switch';
import { Textarea } from '../../../../ui/textarea';
import { toast } from 'sonner';
import {
  PHOTO_PLAN_DEFAULTS,
  PRIMARY_FOCUS_OPTIONS,
  SEARCH_PLAN_CATEGORIES,
  TONE_OPTIONS,
  buildQuickSearchLinks,
  buildSearchPlanText,
  copyTextToClipboard,
  getDayOfYear,
  getPhotoCandidatesErrorMessage,
  getPhotoCandidatesRequestFailureMessage,
  getPhotoImportErrorMessage,
  getPhotoImportRequestFailureMessage,
  getPhotoPlanErrorMessage,
  getPhotoPlanRequestFailureMessage,
  sanitizePhotoPlanPayload,
} from './sidebarPhotoUtils';

const SidebarPhotosSection = ({
  adminFetch,
  openSections,
  setOpenSections,
  styles,
}) => {
  const [photos, setPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [photoPlanForm, setPhotoPlanForm] = useState(PHOTO_PLAN_DEFAULTS);
  const [photoPlanResult, setPhotoPlanResult] = useState(null);
  const [photoPlanError, setPhotoPlanError] = useState('');
  const [photoPlanGenerating, setPhotoPlanGenerating] = useState(false);
  const [lastSubmittedPayload, setLastSubmittedPayload] = useState(null);
  const [showSearchLinks, setShowSearchLinks] = useState(false);
  const [photoCandidatesResult, setPhotoCandidatesResult] = useState(null);
  const [photoCandidatesLoading, setPhotoCandidatesLoading] = useState(false);
  const [photoCandidatesError, setPhotoCandidatesError] = useState('');
  const [hiddenCandidateIds, setHiddenCandidateIds] = useState([]);
  const [keptCandidateIds, setKeptCandidateIds] = useState([]);
  const [showOnlyKept, setShowOnlyKept] = useState(false);
  const [addedCandidateIds, setAddedCandidateIds] = useState([]);
  const [importingKeptCandidates, setImportingKeptCandidates] = useState(false);
  const fileInputRef = useRef(null);

  const fetchPhotos = useCallback(async () => {
    setPhotosLoading(true);
    try {
      const res = await adminFetch('/admin/sidebar-photos');
      if (res.ok) {
        const data = await res.json();
        setPhotos(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch sidebar photos', error);
    } finally {
      setPhotosLoading(false);
    }
  }, [adminFetch]);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  const updatePhotoPlanForm = (patch) => {
    setPhotoPlanForm((prev) => ({ ...prev, ...patch }));
  };

  const toggleTonePreference = (tone) => {
    setPhotoPlanForm((prev) => {
      const exists = prev.tonePreferences.includes(tone);
      if (exists && prev.tonePreferences.length === 1) {
        return prev;
      }
      return {
        ...prev,
        tonePreferences: exists
          ? prev.tonePreferences.filter((item) => item !== tone)
          : [...prev.tonePreferences, tone],
      };
    });
  };

  const generatePhotoPlan = async (payloadOverride = null) => {
    const payload = payloadOverride || sanitizePhotoPlanPayload(photoPlanForm);

    setPhotoPlanGenerating(true);
    setPhotoPlanError('');

    try {
      const res = await adminFetch('/admin/sidebar-photo-search-plan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const message = getPhotoPlanErrorMessage(err?.detail);
        setPhotoPlanError(message);
        toast.error(message);
        return;
      }

      const data = await res.json();
      setPhotoPlanResult(data);
      setLastSubmittedPayload(payload);
      toast.success('Search plan generated');
    } catch (error) {
      const message = getPhotoPlanRequestFailureMessage(error);
      setPhotoPlanError(message);
      toast.error(message);
    } finally {
      setPhotoPlanGenerating(false);
    }
  };

  const handleCopySearchPlan = async () => {
    const text = buildSearchPlanText(photoPlanResult);
    if (!text) {
      toast.error('Generate a plan first');
      return;
    }

    await copyTextToClipboard(text, 'Search plan copied', 'Could not copy search plan', toast);
  };

  const handleRegenerate = () => {
    if (!lastSubmittedPayload) {
      toast.error('Generate a plan first');
      return;
    }

    generatePhotoPlan(lastSubmittedPayload);
  };

  const handleFindSidebarPhotos = async () => {
    const payload = sanitizePhotoPlanPayload(photoPlanForm);

    setPhotoCandidatesLoading(true);
    setPhotoCandidatesError('');

    try {
      const res = await adminFetch('/admin/sidebar-photo-candidates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const message = getPhotoCandidatesErrorMessage(err?.detail);
        setPhotoCandidatesError(message);
        toast.error(message);
        return;
      }

      const data = await res.json();
      setPhotoCandidatesResult(data);
      setHiddenCandidateIds([]);
      setKeptCandidateIds([]);
      setShowOnlyKept(false);
      setAddedCandidateIds([]);
      toast.success(`Found ${data?.candidates?.length || 0} sidebar photo candidates`);
    } catch (error) {
      const message = getPhotoCandidatesRequestFailureMessage(error);
      setPhotoCandidatesError(message);
      toast.error(message);
    } finally {
      setPhotoCandidatesLoading(false);
    }
  };

  const hideCandidate = (candidateId) => {
    setHiddenCandidateIds((prev) => (prev.includes(candidateId) ? prev : [...prev, candidateId]));
    setKeptCandidateIds((prev) => prev.filter((id) => id !== candidateId));
  };

  const resetHiddenCandidates = () => {
    setHiddenCandidateIds([]);
  };

  const toggleKeepCandidate = (candidateId) => {
    setKeptCandidateIds((prev) => (
      prev.includes(candidateId)
        ? prev.filter((id) => id !== candidateId)
        : [...prev, candidateId]
    ));
  };

  const resetKeptCandidates = () => {
    setKeptCandidateIds([]);
  };

  const keptCandidates = useMemo(() => (
    (photoCandidatesResult?.candidates || []).filter(
      (candidate) =>
        keptCandidateIds.includes(candidate.candidateId) && !hiddenCandidateIds.includes(candidate.candidateId)
    )
  ), [hiddenCandidateIds, keptCandidateIds, photoCandidatesResult]);

  const publishableKeptCandidates = useMemo(() => (
    keptCandidates.filter((candidate) => !addedCandidateIds.includes(candidate.candidateId))
  ), [addedCandidateIds, keptCandidates]);

  const handleAddKeptToSidebar = async () => {
    if (publishableKeptCandidates.length === 0) {
      toast.error(keptCandidates.length > 0 ? 'All kept photos are already in the sidebar' : 'Keep at least one photo first');
      return;
    }

    setImportingKeptCandidates(true);

    try {
      const res = await adminFetch('/admin/sidebar-photos/import-candidates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidates: publishableKeptCandidates }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const message = getPhotoImportErrorMessage(err?.detail);
        toast.error(message);
        return;
      }

      const data = await res.json();
      setAddedCandidateIds((prev) => [
        ...prev,
        ...publishableKeptCandidates
          .map((candidate) => candidate.candidateId)
          .filter((candidateId) => !prev.includes(candidateId)),
      ]);
      await fetchPhotos();

      const importedCount = data?.importedCount || 0;
      const skippedCount = data?.skippedCount || 0;
      const summary =
        skippedCount > 0
          ? `Added ${importedCount} photos to the sidebar. Skipped ${skippedCount} already imported item${skippedCount === 1 ? '' : 's'}.`
          : `Added ${importedCount} photos to the sidebar rotation`;
      toast.success(summary);
    } catch (error) {
      const message = getPhotoImportRequestFailureMessage(error);
      toast.error(message);
    } finally {
      setImportingKeptCandidates(false);
    }
  };

  const handleCopyKeptSourceLinks = async () => {
    if (keptCandidates.length === 0) {
      toast.error('Keep at least one visible candidate first');
      return;
    }

    const text = keptCandidates
      .map((candidate) => `[${candidate.category}] ${candidate.query}\n${candidate.photoPageUrl}`)
      .join('\n\n');

    await copyTextToClipboard(text, 'Kept source links copied', 'Could not copy kept source links', toast);
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }

    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await adminFetch('/admin/sidebar-photos', { method: 'POST', body: form });
      if (res.ok) {
        toast.success('Photo uploaded!');
        fetchPhotos();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err?.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    setDeletingId(photoId);
    try {
      const res = await adminFetch(`/admin/sidebar-photos/${photoId}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Photo deleted');
        setPhotos((prev) => prev.filter((photo) => photo.photo_id !== photoId));
      } else {
        toast.error('Failed to delete photo');
      }
    } catch (error) {
      toast.error('Failed to delete photo');
    } finally {
      setDeletingId(null);
    }
  };

  const quickSearchLinks = buildQuickSearchLinks(photoPlanResult);
  const visibleCandidates = (photoCandidatesResult?.candidates || [])
    .filter((candidate) => !hiddenCandidateIds.includes(candidate.candidateId))
    .filter((candidate) => !showOnlyKept || keptCandidateIds.includes(candidate.candidateId))
    .sort((a, b) => {
      const aKept = keptCandidateIds.includes(a.candidateId) ? 1 : 0;
      const bKept = keptCandidateIds.includes(b.candidateId) ? 1 : 0;
      if (aKept !== bKept) return bKept - aKept;
      return b.score - a.score;
    });
  const hiddenCount = (photoCandidatesResult?.candidates || []).length - visibleCandidates.length;
  const keptCount = keptCandidateIds.length;
  const currentLivePhoto = photos.length > 0 ? photos[getDayOfYear() % photos.length] : null;
  const importedLivePhotosCount = photos.filter((photo) => !!photo.imported_from_candidate).length;
  const manualLivePhotosCount = photos.length - importedLivePhotosCount;
  const livePoolLabel = photos.length > 0 ? 'All sidebar photos' : 'No live photos';

  return (
    <>
      <Card className={`${styles.card} order-90 overflow-hidden p-0`}>
        <button
          type="button"
          onClick={() => setOpenSections((current) => (
            current.includes('find-sidebar-photos')
              ? current.filter((item) => item !== 'find-sidebar-photos')
              : [...current, 'find-sidebar-photos']
          ))}
          className="w-full border-b border-gray-200 bg-[linear-gradient(135deg,rgba(36,205,201,0.12),rgba(230,138,90,0.12))] px-6 py-5 text-left dark:border-[#2a2a32]"
        >
          <div className="flex items-start gap-3">
            <span className={styles.disclosureIcon}>
              <ChevronRight className={`h-4 w-4 transition-transform ${openSections.includes('find-sidebar-photos') ? 'rotate-90' : ''}`} />
            </span>
            <div className="min-w-0 flex-1">
              <h3 className={`${styles.sectionTitle} flex items-center gap-2`}>
                <Sparkles className="h-5 w-5 text-aqua" />
                Find Sidebar Photos
              </h3>
              <p className="mt-2 max-w-2xl text-sm text-gray-700 dark:text-gray-300">
                Build a search plan and review candidates when you need new images.
              </p>
            </div>
          </div>
        </button>

        {openSections.includes('find-sidebar-photos') && (
          <div className="space-y-6 p-6">
            <div className="grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
              <div className="space-y-5">
                <div className="grid gap-4 md:grid-cols-4">
                  <div>
                    <label className={`${styles.label} mb-1.5 block`}>Photo count</label>
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={photoPlanForm.photoCount}
                      onChange={(event) => updatePhotoPlanForm({ photoCount: event.target.value })}
                      className={styles.input}
                    />
                  </div>
                  <div>
                    <label className={`${styles.label} mb-1.5 block`}>Primary focus</label>
                    <select
                      value={photoPlanForm.primaryFocus}
                      onChange={(event) => updatePhotoPlanForm({ primaryFocus: event.target.value })}
                      className={styles.select}
                    >
                      {PRIMARY_FOCUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={`${styles.panel} px-4 py-3`}>
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Include brotherhood</p>
                      <Switch
                        checked={photoPlanForm.includeBrotherhood}
                        onCheckedChange={(checked) => updatePhotoPlanForm({ includeBrotherhood: checked })}
                        className="data-[state=checked]:bg-aqua data-[state=unchecked]:bg-[#2a2a32]"
                      />
                    </div>
                  </div>
                  <div className={`${styles.panel} px-4 py-3`}>
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Include diversity</p>
                      <Switch
                        checked={photoPlanForm.includeDiversity}
                        onCheckedChange={(checked) => updatePhotoPlanForm({ includeDiversity: checked })}
                        className="data-[state=checked]:bg-aqua data-[state=unchecked]:bg-[#2a2a32]"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <label className={styles.label}>Tone preferences</label>
                    <span className={styles.subtleText}>Pick at least one</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {TONE_OPTIONS.map((tone) => {
                      const active = photoPlanForm.tonePreferences.includes(tone);
                      return (
                        <button
                          key={tone}
                          type="button"
                          onClick={() => toggleTonePreference(tone)}
                          className={`rounded-full border px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                            active
                              ? 'border-aqua/50 bg-aqua/15 text-aqua'
                              : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:text-gray-900 dark:border-[#2a2a32] dark:bg-[#16161a] dark:text-gray-300 dark:hover:border-[#3c3c44] dark:hover:text-white'
                          }`}
                        >
                          {tone}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className={`${styles.label} mb-1.5 block`}>Notes</label>
                  <Textarea
                    value={photoPlanForm.notes}
                    onChange={(event) => updatePhotoPlanForm({ notes: event.target.value.slice(0, 500) })}
                    placeholder="Optional guidance, like a seasonal angle, setting, or audience nuance."
                    className={`min-h-[120px] ${styles.input}`}
                  />
                  <div className={`mt-2 text-right ${styles.subtleText}`}>{photoPlanForm.notes.length}/500</div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={() => generatePhotoPlan()}
                    disabled={photoPlanGenerating}
                    className="gap-2 bg-aqua text-charcoal hover:bg-aqua-dark"
                  >
                    {photoPlanGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    {photoPlanGenerating ? 'Generating...' : 'Generate Search Plan'}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleFindSidebarPhotos}
                    disabled={photoCandidatesLoading}
                    className="gap-2 bg-[#E68A5A] text-white hover:bg-[#d97a47]"
                  >
                    {photoCandidatesLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Image className="h-4 w-4" />}
                    {photoCandidatesLoading ? 'Finding Photos...' : 'Find Sidebar Photos'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleRegenerate}
                    disabled={photoPlanGenerating || !lastSubmittedPayload}
                    className={`${styles.outlineButton} gap-2`}
                  >
                    <RefreshCw className={`h-4 w-4 ${photoPlanGenerating ? 'animate-spin' : ''}`} />
                    Regenerate
                  </Button>
                </div>

                {photoPlanError && (
                  <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                    {photoPlanError}
                  </div>
                )}

                {photoCandidatesError && (
                  <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                    {photoCandidatesError}
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5 dark:border-[#2a2a32] dark:bg-[#16161a]">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">What this generates</p>
                    <p className={`mt-1 ${styles.subtleText}`}>
                      A human-review search plan for Pexels and Unsplash, tuned for landscape sidebar banners.
                    </p>
                  </div>
                  <div className="rounded-full border border-aqua/20 bg-aqua/10 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-aqua">
                    JSON-backed
                  </div>
                </div>

                <div className="space-y-3">
                  <div className={`${styles.panelSoft} p-4`}>
                    <p className="mb-2 text-xs uppercase tracking-[0.18em] text-gray-500 dark:text-gray-500">Best sources</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">Prioritized free-photo sources with reasoning, not automatic downloads.</p>
                  </div>
                  <div className={`${styles.panelSoft} p-4`}>
                    <p className="mb-2 text-xs uppercase tracking-[0.18em] text-gray-500 dark:text-gray-500">Search phrases</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">Category-by-category search language that matches the Dad Partners brand direction.</p>
                  </div>
                  <div className={`${styles.panelSoft} p-4`}>
                    <p className="mb-2 text-xs uppercase tracking-[0.18em] text-gray-500 dark:text-gray-500">Human review guardrails</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">Clear prefer/reject cues so the final image choice still stays with a person.</p>
                  </div>
                </div>
              </div>
            </div>

            {photoCandidatesResult && (
              <div className="space-y-5 border-t border-gray-200 pt-6 dark:border-[#2a2a32]">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Sidebar photo candidates</h4>
                    <p className={styles.bodyText}>
                      Pexels-first grid of reviewable options. Keep strong candidates at the top and hide anything you do not want to consider.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      onClick={handleAddKeptToSidebar}
                      disabled={importingKeptCandidates || publishableKeptCandidates.length === 0}
                      className="gap-2 bg-aqua text-charcoal hover:bg-aqua-dark"
                    >
                      {importingKeptCandidates ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                      {importingKeptCandidates ? 'Adding Kept...' : `Add Kept To Sidebar${publishableKeptCandidates.length > 0 ? ` (${publishableKeptCandidates.length})` : ''}`}
                    </Button>
                    <div className="rounded-full border border-aqua/20 bg-aqua/10 px-3 py-1.5 text-xs font-medium text-aqua">
                      Visible {visibleCandidates.length}
                    </div>
                    <div className="rounded-full border border-emerald-500/20 bg-emerald-500/[0.08] px-3 py-1.5 text-xs font-medium text-emerald-300">
                      Kept {keptCount}
                    </div>
                    <div className={styles.neutralBadge}>Hidden {hiddenCount}</div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowOnlyKept((prev) => !prev)}
                      className={showOnlyKept ? 'border-emerald-500/30 bg-emerald-500/[0.08] text-emerald-300 hover:bg-emerald-500/[0.12]' : styles.outlineButton}
                    >
                      {showOnlyKept ? 'Showing Kept' : 'View Only Kept'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleCopyKeptSourceLinks}
                      disabled={keptCandidates.length === 0}
                      className={styles.outlineButton}
                    >
                      <Copy className="h-4 w-4" />
                      Copy Kept Links
                    </Button>
                    {keptCount > 0 && (
                      <Button type="button" variant="outline" size="sm" onClick={resetKeptCandidates} className={styles.outlineButton}>
                        Reset Kept
                      </Button>
                    )}
                    {hiddenCount > 0 && (
                      <Button type="button" variant="outline" size="sm" onClick={resetHiddenCandidates} className={styles.outlineButton}>
                        Reset Hidden
                      </Button>
                    )}
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <div className={`${styles.panelDeep} p-4`}>
                    <p className="text-xs uppercase tracking-[0.16em] text-gray-500">Fetched</p>
                    <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">{photoCandidatesResult.searchSummary?.totalFetched || 0}</p>
                  </div>
                  <div className={`${styles.panelDeep} p-4`}>
                    <p className="text-xs uppercase tracking-[0.16em] text-gray-500">Returned</p>
                    <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">{photoCandidatesResult.searchSummary?.totalReturned || 0}</p>
                  </div>
                  <div className={`${styles.panelDeep} p-4`}>
                    <p className="text-xs uppercase tracking-[0.16em] text-gray-500">Queries Used</p>
                    <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">{photoCandidatesResult.searchSummary?.queriesUsed?.length || 0}</p>
                  </div>
                  <div className={`${styles.panelDeep} p-4`}>
                    <p className="text-xs uppercase tracking-[0.16em] text-gray-500">Source</p>
                    <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
                      {(photoCandidatesResult.searchSummary?.sourcesUsed || []).join(', ') || 'Pexels'}
                    </p>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  {SEARCH_PLAN_CATEGORIES.map(({ key, label }) => (
                    <div key={key} className={`${styles.panelDeep} p-4`}>
                      <p className="text-xs uppercase tracking-[0.16em] text-gray-500">{label}</p>
                      <p className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">
                        {photoCandidatesResult.coverage?.[key] ?? 0}
                      </p>
                    </div>
                  ))}
                </div>

                {visibleCandidates.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center dark:border-[#2a2a32] dark:bg-[#121216]">
                    <p className="font-medium text-gray-900 dark:text-white">
                      {showOnlyKept ? 'No kept candidates to show' : 'No visible candidates'}
                    </p>
                    <p className={`mt-2 ${styles.bodyText}`}>
                      {showOnlyKept ? 'Keep a few candidates first, or turn off the kept-only filter.' : 'Reset hidden items or run the search again with different inputs.'}
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                    {visibleCandidates.map((candidate) => (
                      <div key={candidate.candidateId} className="overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 dark:border-[#2a2a32] dark:bg-[#121216]">
                        <div className="aspect-video bg-gray-100 dark:bg-[#16161a]">
                          <img
                            src={candidate.thumbnailUrl}
                            alt={`${candidate.category} candidate from ${candidate.source}`}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        </div>
                        <div className="space-y-3 p-3.5">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="rounded-full border border-aqua/20 bg-aqua/10 px-2.5 py-1 text-[11px] font-medium uppercase tracking-[0.12em] text-aqua">
                              {candidate.category}
                            </span>
                            {addedCandidateIds.includes(candidate.candidateId) && (
                              <span className="rounded-full border border-sky-500/20 bg-sky-500/[0.08] px-2.5 py-1 text-[11px] font-medium uppercase tracking-[0.12em] text-sky-300">
                                Added
                              </span>
                            )}
                            {keptCandidateIds.includes(candidate.candidateId) && (
                              <span className="rounded-full border border-emerald-500/20 bg-emerald-500/[0.08] px-2.5 py-1 text-[11px] font-medium uppercase tracking-[0.12em] text-emerald-300">
                                Kept
                              </span>
                            )}
                            <span className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-medium text-gray-700 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-200">
                              {candidate.source}
                            </span>
                            <span className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-medium text-gray-500 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-400">
                              {candidate.width} x {candidate.height}
                            </span>
                          </div>

                          <div>
                            <p className="text-sm font-medium leading-5 text-gray-900 dark:text-gray-200">{candidate.query}</p>
                            <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-500">{candidate.photographerName || 'Pexels contributor'}</p>
                          </div>

                          <div className="space-y-1.5">
                            {(candidate.reasons || []).slice(0, 2).map((reason) => (
                              <div key={reason} className={styles.mutedPillTight}>
                                {reason}
                              </div>
                            ))}
                          </div>

                          <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-2">
                            <a
                              href={candidate.photoPageUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-aqua/20 bg-aqua/10 px-3 py-2 text-xs font-medium text-aqua transition-colors hover:border-aqua/40 hover:bg-aqua/15"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                              Source
                            </a>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => toggleKeepCandidate(candidate.candidateId)}
                              disabled={addedCandidateIds.includes(candidate.candidateId)}
                              className={`h-auto whitespace-nowrap border-gray-300 bg-white px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 dark:border-[#2a2a32] dark:bg-transparent dark:text-white dark:hover:bg-[#16161a] ${
                                addedCandidateIds.includes(candidate.candidateId)
                                  ? 'border-sky-500/30 bg-sky-500/[0.08] text-sky-300 hover:bg-sky-500/[0.08]'
                                  : keptCandidateIds.includes(candidate.candidateId)
                                    ? 'border-emerald-500/30 bg-emerald-500/[0.08] text-emerald-300 hover:bg-emerald-500/[0.12]'
                                    : ''
                              }`}
                            >
                              <Check className="h-4 w-4" />
                              {addedCandidateIds.includes(candidate.candidateId) ? 'Added' : keptCandidateIds.includes(candidate.candidateId) ? 'Kept' : 'Keep'}
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => hideCandidate(candidate.candidateId)}
                              className="h-auto whitespace-nowrap border-gray-300 bg-white px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 dark:border-[#2a2a32] dark:bg-transparent dark:text-white dark:hover:bg-[#16161a]"
                            >
                              <Trash2 className="h-4 w-4" />
                              Hide
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {photoPlanResult && (
              <div className="space-y-6 border-t border-gray-200 pt-6 dark:border-[#2a2a32]">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Search plan</h4>
                    <p className={styles.bodyText}>Ready to copy into a doc, brief, or image-review workflow.</p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button type="button" variant="outline" onClick={() => setShowSearchLinks((prev) => !prev)} className={`${styles.outlineButton} gap-2`}>
                      <ExternalLink className="h-4 w-4" />
                      {showSearchLinks ? 'Hide Search Links' : 'Generate Search Links'}
                    </Button>
                    <Button type="button" variant="outline" onClick={handleCopySearchPlan} className={`${styles.outlineButton} gap-2`}>
                      <Copy className="h-4 w-4" />
                      Copy Search Plan
                    </Button>
                  </div>
                </div>

                <div>
                  <p className="mb-3 text-sm font-medium text-gray-900 dark:text-white">Best sources</p>
                  <div className="grid gap-3 md:grid-cols-2">
                    {(photoPlanResult.sourcePriority || []).map((source) => (
                      <div key={source.name} className={`${styles.panelDeep} p-4`}>
                        <p className="font-medium text-gray-900 dark:text-white">{source.name}</p>
                        <p className={`mt-2 ${styles.bodyText}`}>{source.reason}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="mb-3 text-sm font-medium text-gray-900 dark:text-white">Suggested photo mix</p>
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    {SEARCH_PLAN_CATEGORIES.map(({ key, label }) => (
                      <div key={key} className={`${styles.panelDeep} p-4`}>
                        <p className="text-xs uppercase tracking-[0.16em] text-gray-500">{label}</p>
                        <p className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">
                          {photoPlanResult.recommendedPhotoMix?.[key] ?? 0}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="mb-3 text-sm font-medium text-gray-900 dark:text-white">Search phrases</p>
                  <div className="grid gap-3 xl:grid-cols-2">
                    {SEARCH_PLAN_CATEGORIES.map(({ key, label }) => (
                      <div key={key} className={`${styles.panelDeep} p-4`}>
                        <p className="mb-3 font-medium text-gray-900 dark:text-white">{label}</p>
                        <div className="space-y-2">
                          {(photoPlanResult.searchPhrasesByCategory?.[key] || []).map((phrase) => (
                            <div key={phrase} className={styles.mutedPill}>
                              {phrase}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {showSearchLinks && (
                  <div>
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">Quick search links</p>
                        <p className={`mt-1 ${styles.subtleText}`}>
                          Start with the two Pexels searches per category. Use the Unsplash backup only if a category needs more options.
                        </p>
                      </div>
                      <p className={styles.subtleText}>12 links total. Opens in a new tab.</p>
                    </div>
                    <div className="grid gap-3 xl:grid-cols-2">
                      {quickSearchLinks.map((group) => (
                        <div key={group.key} className={`${styles.panelDeep} p-4`}>
                          <p className="mb-3 font-medium text-gray-900 dark:text-white">{group.label}</p>
                          <div className="space-y-3">
                            {group.pexelsLinks.map((link, index) => (
                              <div key={`${group.key}-pexels-${index}`} className="rounded-lg bg-white px-3 py-3 dark:bg-white/[0.03]">
                                <div className="flex items-center justify-between gap-3">
                                  <p className="text-sm text-gray-800 dark:text-gray-200">{link.phrase}</p>
                                  <a
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 rounded-full border border-aqua/20 bg-aqua/10 px-3 py-1.5 text-xs font-medium text-aqua transition-colors hover:border-aqua/40 hover:bg-aqua/15"
                                  >
                                    <ExternalLink className="h-3.5 w-3.5" />
                                    Pexels
                                  </a>
                                </div>
                              </div>
                            ))}

                            {group.unsplashLinks.map((link, index) => (
                              <div key={`${group.key}-unsplash-${index}`} className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-3 dark:border-[#2a2a32] dark:bg-black/20">
                                <div className="flex items-center justify-between gap-3">
                                  <p className="text-sm text-gray-700 dark:text-gray-300">{link.phrase}</p>
                                  <a
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-100 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-200 dark:hover:border-white/20 dark:hover:bg-white/[0.08]"
                                  >
                                    <ExternalLink className="h-3.5 w-3.5" />
                                    Unsplash backup
                                  </a>
                                </div>
                              </div>
                            ))}

                            {group.pexelsLinks.length === 0 && group.unsplashLinks.length === 0 && (
                              <div className="rounded-lg bg-white px-3 py-3 text-sm text-gray-500 dark:bg-white/[0.03]">
                                No quick links were generated for this category.
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid gap-4 xl:grid-cols-2">
                  <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.06] p-5">
                    <p className="mb-3 text-sm font-medium text-gray-900 dark:text-white">What to look for</p>
                    <div className="space-y-2">
                      {(photoPlanResult.visualTraitsToPrefer || []).map((item) => (
                        <div key={item} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200">
                          <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-400" />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-rose-500/20 bg-rose-500/[0.06] p-5">
                    <p className="mb-3 text-sm font-medium text-gray-900 dark:text-white">What to reject</p>
                    <div className="space-y-2">
                      {(photoPlanResult.visualTraitsToReject || []).map((item) => (
                        <div key={item} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200">
                          <Trash2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-rose-300" />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5 dark:border-[#2a2a32] dark:bg-[#121216]">
                    <p className="mb-3 text-sm font-medium text-gray-900 dark:text-white">Fast workflow</p>
                    <div className="space-y-3">
                      {(photoPlanResult.fastWorkflow || []).map((item, index) => (
                        <div key={`${index}-${item}`} className="flex gap-3">
                          <div className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-aqua/10 text-xs font-semibold text-aqua">
                            {index + 1}
                          </div>
                          <p className="text-sm text-gray-700 dark:text-gray-300">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {(photoPlanResult.upgradedSearches || []).length > 0 && (
                    <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5 dark:border-[#2a2a32] dark:bg-[#121216]">
                      <p className="mb-3 text-sm font-medium text-gray-900 dark:text-white">Upgraded searches</p>
                      <div className="space-y-2">
                        {(photoPlanResult.upgradedSearches || []).map((item) => (
                          <div key={item} className={styles.mutedPill}>
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      <Card className={`${styles.card} order-80 p-6`}>
        <button
          type="button"
          onClick={() => setOpenSections((current) => (
            current.includes('sidebar-photos')
              ? current.filter((item) => item !== 'sidebar-photos')
              : [...current, 'sidebar-photos']
          ))}
          className="mb-4 flex w-full items-start gap-3 text-left"
        >
          <span className="mt-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-300">
            <ChevronRight className={`h-4 w-4 transition-transform ${openSections.includes('sidebar-photos') ? 'rotate-90' : ''}`} />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className={`${styles.sectionTitle} mb-2`}>Sidebar Photos</h3>
            <p className={styles.bodyText}>
              Upload photos that appear in the sidebar banner. The photo rotates to a new one each day automatically.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className={styles.neutralBadge}>{livePoolLabel}</span>
              <span className={styles.neutralBadge}>{photos.length} total</span>
            </div>
          </div>
        </button>

        {openSections.includes('sidebar-photos') && (
          <>
            <div className="mb-4">
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
              <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="gap-2 bg-aqua text-charcoal hover:bg-aqua-dark">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading ? 'Uploading...' : 'Upload Photo'}
              </Button>
              <span className={`ml-3 ${styles.subtleText}`}>JPG, PNG, or WebP • Max 5MB</span>
            </div>

            <div className="mb-4 grid gap-3 md:grid-cols-3">
              <div className={`${styles.panelDeep} p-4`}>
                <p className="text-xs uppercase tracking-[0.16em] text-gray-500">Today&apos;s photo</p>
                <p className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">{currentLivePhoto?.filename || 'Default sidebar image'}</p>
                <p className={`mt-1 ${styles.subtleText}`}>
                  {currentLivePhoto ? `${currentLivePhoto.imported_from_candidate ? 'Imported' : 'Manual'} • Rotating now` : 'No live photos yet'}
                </p>
              </div>
              <div className={`${styles.panelDeep} p-4`}>
                <p className="text-xs uppercase tracking-[0.16em] text-gray-500">Active pool</p>
                <p className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">{livePoolLabel}</p>
                <p className={`mt-1 ${styles.subtleText}`}>
                  {photos.length} photo{photos.length === 1 ? '' : 's'} rotating in the current daily cycle
                </p>
              </div>
              <div className={`${styles.panelDeep} p-4`}>
                <p className="text-xs uppercase tracking-[0.16em] text-gray-500">Live library</p>
                <p className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
                  {photos.length} total photo{photos.length === 1 ? '' : 's'}
                </p>
                <p className={`mt-1 ${styles.subtleText}`}>{importedLivePhotosCount} imported • {manualLivePhotosCount} manual</p>
              </div>
            </div>

            {photosLoading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-6 w-6 animate-spin text-aqua" />
              </div>
            ) : photos.length === 0 ? (
              <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-500">No photos uploaded yet. The sidebar will use the default image.</p>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {photos.map((photo) => (
                  <div key={photo.photo_id} className={`group relative aspect-video overflow-hidden rounded-lg border bg-gray-100 dark:bg-[#16161a] ${
                    currentLivePhoto?.photo_id === photo.photo_id ? 'border-aqua ring-2 ring-aqua/30' : 'border-transparent'
                  }`}>
                    <img src={photo.url} alt={photo.filename} className="h-full w-full object-cover" />
                    <div className="absolute right-2 top-2 z-10 flex flex-col items-end gap-1">
                      {currentLivePhoto?.photo_id === photo.photo_id && (
                        <span className="rounded-full border border-aqua/60 bg-teal-700/95 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-white shadow-sm">
                          Live Today
                        </span>
                      )}
                      <span className={styles.photoOverlayPill}>{photo.imported_from_candidate ? 'Imported' : 'Manual'}</span>
                    </div>
                    <div className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/30">
                      <button
                        onClick={() => handleDelete(photo.photo_id)}
                        disabled={deletingId === photo.photo_id}
                        className="absolute bottom-2 right-2 rounded-lg bg-red-600/95 p-2 text-white opacity-0 shadow-sm transition-opacity group-hover:opacity-100 hover:bg-red-600"
                      >
                        {deletingId === photo.photo_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      </button>
                    </div>
                    <p className="absolute bottom-0 left-0 right-0 truncate bg-black/50 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
                      {photo.filename}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Card>
    </>
  );
};

export default SidebarPhotosSection;
